import React, { useEffect, useState } from 'react';
import { Button } from '../../shadComponents/ui/button';
import { Input } from '../../shadComponents/ui/input';

export interface RuntimeState {
    instanceId: string;
    screenId: string;
    supportsPersistence?: boolean;
    persistenceError?: string | null;
    variables: { key: string; value: string; overridden: boolean; persisted?: boolean }[];
}

type SetVariable = (instanceId: string, key: string, value: string | null, persist?: boolean) => Promise<boolean>;

function VariableRow({ variable, instanceId, disabled, supportsPersistence, onSet }: {
    variable: RuntimeState['variables'][number];
    instanceId: string;
    disabled: boolean;
    supportsPersistence: boolean;
    onSet: SetVariable;
}) {
    const [draft, setDraft] = useState<string | null>(null);
    const [sending, setSending] = useState(false);
    const [persistDraft, setPersistDraft] = useState<boolean | null>(null);
    // Fresh reports update the current value without discarding an operator's edit.
    useEffect(() => { setDraft((current) => current === variable.value ? null : current); }, [variable.value]);
    useEffect(() => { setPersistDraft((current) => current === !!variable.persisted ? null : current); }, [variable.persisted]);
    const send = async (value: string | null) => {
        setSending(true);
        try {
            const persist = value !== null && (persistDraft ?? !!variable.persisted);
            const queued = supportsPersistence
                ? await onSet(instanceId, variable.key, value, persist)
                : await onSet(instanceId, variable.key, value);
            if (queued && value === null) { setDraft(null); setPersistDraft(null); }
        }
        finally { setSending(false); }
    };
    return (
        <fieldset disabled={disabled || sending} className="flex flex-wrap items-center gap-2 border-b py-3 last:border-0">
            <div className="min-w-40 flex-1">
                <div className="break-all font-mono text-sm">{variable.key}</div>
                <div className="break-all text-xs text-muted-foreground">Current: {variable.value || '(empty)'}</div>
                {variable.overridden && <div className="text-xs text-muted-foreground">{variable.persisted ? 'Saved on this booth' : 'Temporary override'}</div>}
            </div>
            <Input
                className="w-48" aria-label={`Value for ${variable.key}`}
                value={draft ?? variable.value} maxLength={4096} disabled={disabled || sending}
                onChange={(event) => setDraft(event.target.value)}
            />
            {supportsPersistence && <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" aria-label={`Persist ${variable.key}`} checked={persistDraft ?? !!variable.persisted}
                    onChange={(event) => setPersistDraft(event.target.checked)} />
                Persist
            </label>}
            <Button size="sm" onClick={() => send(draft ?? variable.value)} aria-label={`Apply ${variable.key}`}>Apply</Button>
            {variable.overridden && (
                <Button size="sm" variant="outline" onClick={() => send(null)} aria-label={`Release ${variable.key}`}>Release</Button>
            )}
        </fieldset>
    );
}

export default function RuntimeVariables({ runtime, disabled, onSet }: {
    runtime?: RuntimeState | null;
    disabled: boolean;
    onSet: SetVariable;
}) {
    const [name, setName] = useState('');
    const [value, setValue] = useState('true');
    const [sending, setSending] = useState(false);
    const [persist, setPersist] = useState(false);
    useEffect(() => { setName(''); setValue('true'); setPersist(false); }, [runtime?.instanceId]);

    if (runtime === undefined) return <p className="text-sm text-muted-foreground">This device has not reported an experience or does not support runtime variables. Connect with an updated Clickerbooth app.</p>;
    if (runtime === null) return <p className="text-sm text-muted-foreground">Open an experience on this device to view and change its variables.</p>;

    const add = async (event: React.FormEvent) => {
        event.preventDefault();
        if (disabled || sending || !name.trim()) return;
        setSending(true);
        try {
            const queued = runtime.supportsPersistence
                ? await onSet(runtime.instanceId, name.trim(), value, persist)
                : await onSet(runtime.instanceId, name.trim(), value);
            if (queued) setName('');
        } finally { setSending(false); }
    };

    return (
        <div className="space-y-3">
            <p className="text-sm text-muted-foreground">Text variables for the current experience. Use true or false for feature switches. Temporary overrides last until the experience closes.</p>
            {runtime.supportsPersistence && <p className="text-sm text-muted-foreground">Check Persist and apply to remember a value for this activation on this booth, including after restarts. Release removes the saved value too.</p>}
            {runtime.persistenceError && <p role="alert" className="text-sm text-destructive">{runtime.persistenceError}</p>}
            <p className="text-xs text-muted-foreground">Screen: {runtime.screenId}. Values update when reported by the booth; queued changes appear in Change History.</p>
            {runtime.variables.length === 0 && <p className="text-sm">No text variables yet. Add a feature switch below.</p>}
            <div>
                {runtime.variables.map((variable) => (
                    <VariableRow key={`${runtime.instanceId}:${variable.key}`} variable={variable} instanceId={runtime.instanceId} disabled={disabled} supportsPersistence={!!runtime.supportsPersistence} onSet={onSet} />
                ))}
            </div>
            <form onSubmit={add}>
                <fieldset disabled={disabled || sending} className="flex flex-wrap gap-2">
                    <Input className="w-56" aria-label="Variable name" placeholder="Variable name" value={name} maxLength={128} onChange={(event) => setName(event.target.value)} />
                    <Input className="w-48" aria-label="New variable value" value={value} maxLength={4096} onChange={(event) => setValue(event.target.value)} />
                    {runtime.supportsPersistence && <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" aria-label="Persist new variable" checked={persist} onChange={(event) => setPersist(event.target.checked)} />
                        Persist
                    </label>}
                    <Button type="submit" size="sm" disabled={!name.trim()}>Add override</Button>
                </fieldset>
            </form>
        </div>
    );
}
